import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  beginHistoryTransaction,
  cancelHistoryTransaction,
  commitHistoryTransaction,
} from "../../app/slices/HistorySlice";
import {
  addInteractionCopy,
  setPrimaryInteraction,
  setSelectedInteractions,
} from "../../app/slices/TrainingSlice";
import Interaction from "../../models/interactions/Interaction";
import { createClipboardPasteTransactionId } from "./copyPasteClipboard";
import { useCopyInteraction } from "./CopyInteractionHook";

/**
 * Duplicates one or more top-level interactions of the selected Take.
 * A group duplication runs in a single history transaction, chains the
 * copies after the last group member in source order, and selects the new
 * copies. Returns the number of copies created.
 */
export function useDuplicateInteractions() {
  const dispatch = useDispatch();
  const { getInteractionCopy } = useCopyInteraction();

  return useCallback(
    (sources: Interaction[]): number => {
      if (sources.length === 0) return 0;

      if (sources.length === 1) {
        const copy = getInteractionCopy(sources[0]);
        if (!copy) return 0;
        dispatch(addInteractionCopy(copy));
        return 1;
      }

      const lastMember = sources[sources.length - 1];
      const transaction = { id: createClipboardPasteTransactionId() };
      dispatch(beginHistoryTransaction(transaction));
      try {
        dispatch(setPrimaryInteraction(lastMember.id));
        const copies: Interaction[] = [];
        for (const source of sources) {
          const copy = getInteractionCopy(source);
          if (copy) {
            dispatch(addInteractionCopy(copy));
            copies.push(copy);
          }
        }
        if (copies.length === 0) {
          dispatch(cancelHistoryTransaction(transaction));
          return 0;
        }
        dispatch(setSelectedInteractions(copies.map((copy) => copy.id)));
        dispatch(commitHistoryTransaction(transaction));
        return copies.length;
      } catch (error) {
        dispatch(cancelHistoryTransaction(transaction));
        throw error;
      }
    },
    [dispatch, getInteractionCopy]
  );
}
